import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Paper, Container } from '@mui/material';
import { CircularProgress, Button, List, ListItem, ListItemSecondaryAction, ListItemText, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Skeleton } from '@mui/material';
import EditIcon from '@mui/icons-material/DriveFileRenameOutline';
import ExtendIcon from '@mui/icons-material/MoreTime';
import TashIcon from '@mui/icons-material/Delete';
import DraftIcon from '@mui/icons-material/VisibilityOff';
import PublishIcon from '@mui/icons-material/Flag';
import CountdownTimer from "../../../components/UI/CountdownTimer";
import { Link } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import MUIConfirm from '../../../components/UI/MUIConfirm';
import MUIAlert from '../../../components/UI/MUIAlert';
import Divider from '@mui/material/Divider';
import { getAuctions } from "../../../api/auctions";
import { Helmet } from 'react-helmet-async';

import { deleteAuctionById, publishAuctionAdmin, draftAuction, extendAuction } from "../../../api/auctions";

const AuctionManagement = () => {

    const theme = useTheme();
    const [error, setError] = useState(null);
    const { t } = useTranslation();
    const [auctions, setAuctions] = useState([]);
    const [loading, setLoading] = useState(true); // Add loading state
    const [submittingExtend, setSubmittingExtend] = useState(false); // Add loading state
    const [submittingPublish, setSubmittingPublish] = useState(false); // Add loading state
    const [submittingDraft, setSubmittingDraft] = useState(false); // Add loading state
    const [submittingDelete, setSubmittingDelete] = useState(false); // Add loading state

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmContent, setConfirmContent] = useState('');
    const [confirmCallback, setConfirmCallback] = useState(null);
    const [confirmParameter, setconfirmParameter] = useState(null);

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('success'); // Default severity

    const showAlert = (message, severity = 'success') => {
        setAlertMessage(message);
        setAlertSeverity(severity);
        setAlertOpen(true);
    };

    const handleAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertOpen(false);
    };

    const showConfirm = (title, content, onConfirm, parameter) => {
        setConfirmTitle(title);
        setConfirmContent(content);
        setConfirmCallback(() => onConfirm);
        setconfirmParameter(parameter)
        setConfirmOpen(true);
    };

    const handleConfirmClose = () => {
        setConfirmOpen(false);
        setConfirmTitle('');
        setConfirmContent('');
        setConfirmCallback(null);
        setconfirmParameter(null)
    };

    const handleConfirmAction = () => {
        if (confirmCallback && confirmParameter) {
            confirmCallback(confirmParameter);
        }
        handleConfirmClose();
    };

    const handleDeleteAuction = async (auctionId) => {
        showConfirm(
            t("my_auctions.delete_confirmation_title"),
            t("my_auctions.delete_confirmation"),
            async (auctionId) => {
                setSubmittingDelete(true);
                try {
                    await deleteAuctionById(auctionId);
                    fetchAuctions(setAuctions, setLoading, setError);
                    showAlert(t("my_auctions.delete_success"));
                    setSubmittingDelete(false);
                } catch (error) {
                    showAlert(t("my_auctions.delete_failure"), 'error');
                    console.error("Failed to delete auction:", error);
                    setError('Failed to delete auction.');
                    setSubmittingDelete(false);
                }
            },
            auctionId // Pass the auctionId to the showConfirm function
        );
    };

    const handlePublishAuction = async (auctionId) => {
        showConfirm(
            t("my_auctions.publish_confirmation_title"),
            t("my_auctions.publish_confirmation"),
            async (auctionId) => {
                setSubmittingPublish(true);
                try {
                    await publishAuctionAdmin(auctionId);
                    fetchAuctions(setAuctions, setLoading, setError);
                    showAlert(t("my_auctions.publish_success"));
                    setSubmittingPublish(false);
                } catch (error) {
                    showAlert(t("my_auctions.publish_failure"), 'error');
                    console.error("Failed to publish auction:", error);
                    setError('Failed to publish auction.');
                    setSubmittingPublish(false);
                }
            }, auctionId)
    };

    const handleDraftAuction = async (auctionId) => {
        showConfirm(
            t("my_auctions.draft_confirmation_title"),
            t("my_auctions.draft_confirmation"),
            async (auctionId) => {
                setSubmittingDraft(true);
                try {
                    await draftAuction(auctionId);
                    fetchAuctions(setAuctions, setLoading, setError);
                    showAlert(t("my_auctions.draft_success"));
                    setSubmittingDraft(false);
                } catch (error) {
                    showAlert(t("my_auctions.draft_failure"), 'error');
                    console.error("Failed to draft auction:", error);
                    setError('Failed to draft auction.');
                    setSubmittingDraft(false);
                }
            }, auctionId)
    };

    const handleExtendAuction = async (auctionId) => {
        showConfirm(
            t("my_auctions.extend_confirmation_title"),
            t("my_auctions.extend_confirmation"),
            async (auctionId) => {
                setSubmittingExtend(true); // Set loading to true while extending
                try {
                    await extendAuction(auctionId);
                    fetchAuctions(setAuctions, setLoading, setError);
                    showAlert(t("my_auctions.extend_success"));
                    setSubmittingExtend(false); // Set loading to true while extending
                } catch (error) {
                    showAlert(t("my_auctions.extend_failure"), 'error');
                    console.error("Failed to extend auction:", error);
                    setError('Failed to extend auction.');
                    setSubmittingExtend(false); // Set loading to true while extending
                }
            }, auctionId)

    };

    const fetchAuctions = async (setAuctions, setLoading, setError) => {
        try {
            const fetchedAuctions = await getAuctions();
            setAuctions(fetchedAuctions);
            setLoading(false); // Set loading to false after data is fetched
        } catch (error) {
            console.error("Failed to fetch auctions:", error);
            setError("Failed to fetch auctions.");
            setLoading(false); // Set loading to false on error as well
        }
    };

    useEffect(() => {
        fetchAuctions(setAuctions, setLoading, setError);
    }, []);


    return (
        <>
            <Helmet>
                <meta name="robots" content="noindex" />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <Container maxWidth="lg">

                <Container sx={{ mt: 4, mb: 4 }}>
                    <Box>
                        <Box sx={{ position: "relative" }}>
                            <Typography variant="h4" gutterBottom>
                                {t("admin.auctions")}
                            </Typography>

                            {loading ? ( // Show skeleton for active auctions
                                <Paper sx={{ padding: 2, marginBottom: 4 }}>
                                    <Typography variant="h6" gutterBottom><Skeleton width="40%" /></Typography>
                                    <List>
                                        {Array.from({ length: 2 }).map((_, index) => (
                                            <ListItem key={index} sx={{ marginBottom: 1 }}>
                                                <ListItemText
                                                    primary={<Skeleton width="60%" />}
                                                    secondary={<Skeleton width="40%" />}
                                                />
                                                <ListItemText
                                                    primary={<Skeleton width="30%" />}
                                                    secondary={<Skeleton width="30%" />}
                                                />
                                                <ListItemSecondaryAction>
                                                    <Skeleton width="100px" height="40px" style={{ marginRight: 1 }} />
                                                    <Skeleton width="80px" height="40px" style={{ marginRight: 1 }} />
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            ) : auctions.length > 0 ? (
                                <Paper sx={{ padding: 2, marginBottom: 4 }}>
                                    <List>
                                        {auctions.map((auction, index) => {
                                            const isLast = index === auctions.length - 1;
                                            return (<>
                                                <ListItem
                                                    button
                                                    key={auction.auction_id}
                                                    component={Link}
                                                    to={`/izsole/${auction.auction_id}`}
                                                    sx={{
                                                        marginBottom: 1,
                                                    }}
                                                >
                                                    <ListItem
                                                        button
                                                        key={auction.auction_id}
                                                        component={Link}
                                                        to={`/izsole/${auction.auction_id}`}
                                                        sx={{ marginBottom: 1 }}
                                                    >
                                                        <ListItemText
                                                            primary={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                    <Typography variant="body1" fontWeight={500}>
                                                                        {t("general.auction")} {auction.auction_id}
                                                                    </Typography>
                                                                    {auction.status == "active" ?
                                                                        <Box
                                                                            component="span"
                                                                            sx={{
                                                                                backgroundColor: theme.palette.success.light,
                                                                                color: theme.palette.success.contrastText,
                                                                                borderRadius: '6px',
                                                                                px: 1,
                                                                                fontSize: '0.75rem',
                                                                                fontWeight: 500,
                                                                                whiteSpace: 'nowrap',
                                                                            }}
                                                                        >
                                                                            {auction.status}
                                                                        </Box>
                                                                        :
                                                                        <Box
                                                                            component="span"
                                                                            sx={{
                                                                                backgroundColor: theme.palette.grey[300],
                                                                                color: theme.palette.text.primary,
                                                                                borderRadius: '6px',
                                                                                px: 1,
                                                                                fontSize: '0.75rem',
                                                                                fontWeight: 500,
                                                                                whiteSpace: 'nowrap',
                                                                            }}
                                                                        >
                                                                            {auction.status}
                                                                        </Box>
                                                                    }
                                                                </Box>
                                                            }
                                                            secondary={auction.status == "active" ?
                                                                <Box sx={{ mt: 0.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                                                    <Typography variant="body2" color="text.primary">
                                                                        €{(Number(auction.starting_price) + Number(auction.highest_bid)).toLocaleString()}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {t("my_auctions.start_price")}: {auction.starting_price} €
                                                                    </Typography>
                                                                    <CountdownTimer endTime={auction.end_time} />
                                                                </Box>
                                                                : null}
                                                            // TODO show invoice number and status

                                                            sx={{ color: theme.palette.text.primary }}
                                                        />
                                                    </ListItem>


                                                    <ListItemSecondaryAction>
                                                        {auction.status == "active" ?
                                                            <Button
                                                                variant="outlined"
                                                                color="primary"
                                                                onClick={() => handleExtendAuction(auction.auction_id)}
                                                                sx={{ marginRight: 1 }}
                                                                endIcon={<ExtendIcon />}
                                                            >

                                                                {submittingExtend ? (
                                                                    <CircularProgress size={24} color="inherit" /> // Show spinner when loading
                                                                ) : (
                                                                    t("my_auctions.extend")
                                                                )}
                                                            </Button> : null}


                                                        {auction.status == "inactive" ?
                                                            <Button
                                                                variant="outlined"
                                                                color="primary"
                                                                endIcon={<PublishIcon />}
                                                                onClick={() => handlePublishAuction(auction.auction_id)}
                                                                sx={{ marginRight: 1 }}
                                                            >
                                                                {submittingPublish ? (
                                                                    <CircularProgress size={24} color="inherit" /> // Show spinner when loading
                                                                ) : (
                                                                    t("my_auctions.publish")
                                                                )}
                                                            </Button> : null}

                                                        {auction.status == "active" ?
                                                            <Button
                                                                variant="outlined"
                                                                color="primary"
                                                                endIcon={<DraftIcon />}
                                                                onClick={() => handleDraftAuction(auction.auction_id)}
                                                                sx={{ marginRight: 1 }}
                                                            >
                                                                {submittingDraft ? (
                                                                    <CircularProgress size={24} color="inherit" /> // Show spinner when loading
                                                                ) : (
                                                                    t("my_auctions.draft")
                                                                )}
                                                            </Button>
                                                            : null}
                                                        <Button
                                                            variant="outlined"
                                                            color="primary"
                                                            component={Link}
                                                            endIcon={<EditIcon />}
                                                            to={`/labot/${auction.auction_id}`}
                                                            sx={{ marginRight: 1 }}
                                                        >
                                                            {t("my_auctions.edit")}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            endIcon={<TashIcon />}
                                                            onClick={() => handleDeleteAuction(auction.auction_id)}
                                                        >
                                                            {submittingDelete ? (
                                                                <CircularProgress size={24} color="inherit" />
                                                            ) : (
                                                                t("my_auctions.delete")
                                                            )}

                                                        </Button>
                                                    </ListItemSecondaryAction>

                                                </ListItem>
                                                {!isLast ? <Divider orientation="horizontal" flexItem /> : null}

                                            </>)

                                        })}
                                    </List>
                                </Paper>
                            ) : (
                                <></>
                            )}
                        </Box>
                    </Box>
                </Container>

                <MUIConfirm
                    open={confirmOpen}
                    onClose={handleConfirmClose}
                    onConfirm={handleConfirmAction}
                    title={confirmTitle}
                    content={confirmContent}
                />
                <MUIAlert
                    open={alertOpen}
                    onClose={handleAlertClose}
                    severity={alertSeverity}
                    message={alertMessage}
                />
            </Container>
        </>
    );

};

export default AuctionManagement;
